/**
 * The clinic, as the Details page edits it: the one `Organization` per
 * project with a `FHIR_IDENTIFIER_SYSTEMS.CLINIC_DETAILS` identifier, which
 * AKB writes from the clinic details sheet. This file holds only data
 * description, no runtime logic.
 */
import Building2 from "lucide-react/dist/esm/icons/building-2";

import type { ConfigSchema } from "@/types/clinic-configuration";
import {
  FHIR_EXTENSIONS,
  FHIR_IDENTIFIER_SYSTEMS,
} from "@/constants/fhir.constants";
import { ROUTES } from "@/constants/routes.constants";
import { fhirLanguageLabels } from "@/content/clinic-configuration";

/**
 * Needs for i18next-scanner
 */
// t('Details')
// t('Name')
// t('Tagline')
// t('Email')
// t('Country')
// t('A two-letter country code, e.g. CO')
// t('Timezone')
// t('Language')
// t('Clinic ID')
export const clinicDetailsSchema = {
  fields: {
    name: {
      kind: "text",
      path: "name",
      title: "Name",
      required: true,
      orientation: "horizontal",
    },
    email: {
      kind: "text",
      path: "telecom.value",
      match: { system: "email" },
      title: "Email",
      orientation: "horizontal",
    },
    country: {
      kind: "text",
      path: "address.0.country",
      title: "Country",
      description: "A two-letter country code, e.g. CO",
      orientation: "horizontal",
      appearance: "subtle",
    },
    /**
     * Read-only: it is not the clinic's choice which language AKB wrote
     * the record in, and nothing here reads it back.
     */
    language: {
      kind: "code",
      path: "language",
      title: "Language",
      labels: fhirLanguageLabels,
      readonly: true,
      orientation: "horizontal",
    },
    /**
     * Read-only: every location AKB writes points at the clinic by this
     * value (`managingOrganization.identifier`), not by id, so changing it
     * would cut the clinic off from its locations.
     */
    clinicId: {
      kind: "text",
      path: "identifier.value",
      match: { system: FHIR_IDENTIFIER_SYSTEMS.CLINIC_DETAILS },
      title: "Clinic ID",
      readonly: true,
      orientation: "horizontal",
    },
  },
  extensions: {
    tagline: {
      kind: "text",
      slug: FHIR_EXTENSIONS.CLINIC_DETAILS_TAGLINE,
      valueType: "string",
      title: "Tagline",
      layout: "inline",
      orientation: "horizontal",
    },
    timezone: {
      kind: "timezone",
      slug: FHIR_EXTENSIONS.TIMEZONE,
      title: "Timezone",
      orientation: "horizontal",
      appearance: "subtle",
    },
  },
} satisfies ConfigSchema.Resource;

export const clinicDetailsPage = {
  to: ROUTES.CLINIC_DETAILS,
  label: "Details",
  icon: Building2,
} satisfies ConfigSchema.Page;
